import { useState, type ReactNode } from 'react';
import type { ThemeTokens } from '@/shared/ui/theme/tokens';

interface ActionButtonProps {
  label: string;
  sublabel?: string;
  color: string;
  icon: ReactNode;
  onClick: () => void;
  T: ThemeTokens;
  /**
   * Layout del botón — controla el flex-direction y las dimensiones internas.
   *
   *   'column' (default) — dos botones lado a lado (Entrada / Salida):
   *     161 px de alto · chip 60×60 · label 14/700 · sublabel 10/400 · gap 14
   *
   *   'row' — botón único full-width (modo automático / botón único):
   *     120 px de alto · chip 72×72 · label 21/700 · sublabel 13/400 · gap 18
   *     padding 24×28 · flex-direction row · texto alineado a la izquierda
   *
   * Medidas canónicas del mockup `web/Clock Web.html` `.action-card`
   * (columna) y `.action-card-single` (row) — extraídas 2026-07-16.
   */
  layout?: 'column' | 'row';
}

export function ActionButton({ label, sublabel, color, icon, onClick, T: _T, layout = 'column' }: ActionButtonProps) {
  const [hov, setHov] = useState(false);
  const isRow = layout === 'row';

  // Dimensiones canónicas por layout — evita ramificar el JSX y hace el
  // switch trivial de rastrear.
  const dims = isRow
    ? { height: 120, padding: '24px 28px', gap: 18, chip: 72, labelSize: 21, subSize: 13, textAlign: 'left' as const }
    : { height: 161, padding: '27px 21px', gap: 14, chip: 60, labelSize: 14, subSize: 10, textAlign: 'center' as const };

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => { setHov(true); }}
      onMouseLeave={() => { setHov(false); }}
      style={{
        // En layout row el botón ocupa TODO el ancho disponible (full-width);
        // en column mantiene el flex:1 clásico para colapsarse dentro del grid.
        width: isRow ? '100%' : undefined,
        flex: isRow ? undefined : 1,
        height: dims.height,
        border: 'none',
        borderRadius: 15,
        padding: dims.padding,
        cursor: 'pointer',
        fontFamily: 'inherit',
        background: color,
        color: '#fff',
        display: 'flex',
        flexDirection: isRow ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: dims.gap,
        transition: 'transform .22s ease, box-shadow .22s ease',
        transform: hov ? 'translateY(-2px)' : 'none',
        boxShadow: hov
          ? `0 10px 26px ${color}47`
          : `0 6px 20px ${color}33`,
      }}
    >
      {/* Chip circular del ícono — `flexShrink: 0` es non-negotiable dentro
          del flex parent para que el navegador respete el aspect-ratio 1:1. */}
      <div
        style={{
          width: dims.chip,
          height: dims.chip,
          flexShrink: 0,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.22)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <div style={{ textAlign: dims.textAlign }}>
        <div style={{ fontSize: dims.labelSize, fontWeight: 700 }}>{label}</div>
        {sublabel && (
          <div style={{ fontSize: dims.subSize, opacity: 0.7, marginTop: 4 }}>{sublabel}</div>
        )}
      </div>
    </button>
  );
}
